import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LoginPopup } from "../../common/components";
import { adminLogin } from "../../api/auth";
import { routes } from "../../constants/routes";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertComponent } from "../../common/components";
import Dashboard from "./features/dashboard/dashboard";
import loginBackground from "../../assets/media/admin-login-bg-image.jpeg";
import { useSetLoading } from "../../common/hooks/appearance";
import { handleLogin } from "../../common/routines/user-operations";

const AdminSection: React.FC = () => {
  const superAdminDetails = useSelector(
    (state: any) => state.session.superAdminDetails
  );
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const location = useLocation();

  const checkIsAdminLogin = () => {
    const raw = localStorage.getItem("superAdminDetails");
    if (!raw) return false;

    const stored = JSON.parse(raw);

    // must have token
    if (!stored?.accessToken) return false;

    // if expiryTime exists, enforce it
    if (stored?.expiryTime && stored.expiryTime <= Date.now()) return false;

    if (
      superAdminDetails &&
      (superAdminDetails?.user?.role === "Super Admin" || superAdminDetails?.user?.role === "Admin") &&
      superAdminDetails?.accessToken
    ) {

      return true;
    }
    return false;
  };


  const mapErrorMessage = (error: any): string => {
    const message = error?.response?.data?.message;
    const errors = error?.response?.data?.errors;

    // 1. Handle input validation errors
    if (message === "Input validation failed" && Array.isArray(errors)) {
      const messages: string[] = [];

      errors.forEach((errObj: any) => {
        if (errObj.email) messages.push("Invalid Email Address");
        if (errObj.password) messages.push("Invalid Password");
      });

      return messages.join(" and ");  // e.g., "Invalid Email Address and Invalid Password"
    }

    // 2. Handle specific backend messages
    switch (message) {
      case "ADMIN_USER_NOT_FOUND":
        return "Admin user not found";

      case "ADMIN_PASSWORD_NOT_SET":
        return "Password is not set for this admin user";

      case "INVALID_ADMIN_CREDENTIALS":
        return "Invalid Password";

      case "Too many requests. Please try again later.":
        return "Too many requests. Please try again later.";

      case "No admin user found with the given email.":
        return "No admin user found with the given email.";

      default:
        return "Login failed. Please try again.";
    }
  };


  const handleLoginAPI = async (email: string, password: string) => {
    setLoading(true);
    setError("");

    try {
      const response = await adminLogin({ email, password });
      if (response?.data?.success === true && response?.data) {
        const userData = response?.data?.data;
        handleLogin(userData);
        setAlertMessage("Admin has logged in successfully.");
        setAlertType("success");
        setAlertOpen(true);
        // Delay navigation to allow toast to render
        setTimeout(() => {
          navigate(routes.ADMIN_MANAGEMENT, { replace: true, state: {} });
        }, 100);
      }
      setTimeout(() => {
        setAlertOpen(false);
      }, 1000);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || "Login failed");
      const friendlyMessage = mapErrorMessage(err);
      setAlertMessage(friendlyMessage || "Login failed. Please try again.");
      setError(friendlyMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const reason = localStorage.getItem("superAdminLogoutReason");
    if (reason) {
      if (reason === "superAdmin:INACTIVITY" || reason === "superAdmin:SESSION_EXPIRED") {
        setAlertMessage("Your session has expired. Please log in again.");
        setAlertType("info");
      } else if (reason === "superAdmin:ACCOUNT_INACTIVE") {
        setAlertMessage("Your account has been deactivated. Please contact support.");
        setAlertType("error");
      }

      if (
        reason === "superAdmin:INACTIVITY" ||
        reason === "superAdmin:SESSION_EXPIRED" ||
        reason === "superAdmin:ACCOUNT_INACTIVE"
      ) {
        localStorage.removeItem("superAdminLogoutReason");

        dispatch({
          type: "SESSION:SET_AUTO_LOGOUT",
          payload: { userType: "superAdmin", autoLogoutOfSuperAdmin: false },
        });

        setAlertOpen(true);

        setTimeout(() => {
          setAlertOpen(false);
        }, 3000);
      }
    }
  }, [superAdminDetails, dispatch]);

  useEffect(() => {
    //if user is already logged in, and enter login url redirect to admin dashboard
    if (checkIsAdminLogin() && location.pathname !== routes.ADMIN_MANAGEMENT) {
      navigate(routes.ADMIN_MANAGEMENT, { replace: true, state: {} });
    }
    //to show logout success message after redirecting to login page
    if (location.state && location?.state?.fromLogout) {
      setAlertMessage(location?.state?.alertMessage);
      setAlertType(location?.state?.alertType);
      setAlertOpen(true);
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 1000);
    }
  }, [location.pathname, location.state]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  // If not logged in, show login popup
  if (!checkIsAdminLogin()) {
    return (
      <>
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundImage: `url(${loginBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="admin-login-wrapper"
        >
          <LoginPopup
            open={!checkIsAdminLogin()}
            onClose={() => setError("")}
            onLogin={handleLoginAPI}
            loading={loading}
            error={error}
            headerText="MemorialGo Admin Portal"
            bodyText="Admin Login"
            bodyContent="Enter your credentials to continue"
          />
        </div>
        <AlertComponent
          message={alertMessage}
          type={alertType ?? "info"}
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
        />
      </>
    );
  }

  // If logged in, show admin content
  return (
    <div className="admin-dashboard">
      <Dashboard />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default AdminSection;
